import { useEffect, useState } from 'react';
import {
  getIframePoolOfferItems,
  getIframePoolOfferOdds,
  getPoolOffer,
  listAvailableCategories,
  type ApiClient,
  type AvailableCategory,
  type IframePoolItem,
  type IframePoolOfferOdds,
  type PoolOfferDetail,
} from '../../../modules/api';

export interface PackDataState {
  offer: PoolOfferDetail | null;
  categories: AvailableCategory[];
  odds: IframePoolOfferOdds | null;
  maxForPurchaseCount: number | undefined;
  bgImageUrl: string | null;
  potentialHits: IframePoolItem[];
  potentialHitsLoading: boolean;
  loading: boolean;
  error: string | null;
}

/**
 * Parallel fetch for the pack-detail page — identical to the classic
 * template's `usePackData`, kept as a separate file so the modern template
 * owns its own data hook and can diverge (e.g. carousel pre-fetch) later.
 */
export function usePackData(client: ApiClient, gameId: string | null): PackDataState {
  const [offer, setOffer] = useState<PoolOfferDetail | null>(null);
  const [categories, setCategories] = useState<AvailableCategory[]>([]);
  const [odds, setOdds] = useState<IframePoolOfferOdds | null>(null);
  const [potentialHits, setPotentialHits] = useState<IframePoolItem[]>([]);
  const [potentialHitsLoading, setPotentialHitsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void listAvailableCategories(client).then((cats) => {
      if (!cancelled) setCategories(cats);
    });
    return () => {
      cancelled = true;
    };
  }, [client]);

  useEffect(() => {
    if (gameId === null) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setOdds(null);
    setPotentialHits([]);
    setPotentialHitsLoading(true);

    getPoolOffer(client, gameId)
      .then((o) => {
        if (cancelled) return;
        setOffer(o);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to load pack';
        setError(message);
        setLoading(false);
      });

    getIframePoolOfferOdds(client, gameId)
      .then((o) => {
        if (!cancelled) setOdds(o);
      })
      .catch(() => {
        /* odds are optional — panel hides if they don't load */
      });

    getIframePoolOfferItems(client, gameId)
      .then((page) => {
        if (cancelled) return;
        setPotentialHits(page.items);
        setPotentialHitsLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setPotentialHitsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [client, gameId]);

  return {
    offer,
    categories,
    odds,
    maxForPurchaseCount: odds ? odds.maxForPurchaseCount : undefined,
    bgImageUrl: odds?.bgImageUrl ?? null,
    potentialHits,
    potentialHitsLoading,
    loading,
    error,
  };
}
